var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, css } from 'lit';
import { property } from 'lit/decorators.js';
import { getResolvedTheme, onThemeChange } from '../theme';
export const deviceIconStyles = css `
  :host {
    display: inline-block;
    line-height: 0;
    /* status LEDs — theme-independent */
    --green: #22c55e;
    --red: #ef4444;
    --blue: #3b82f6;
    --amber: #f59e0b;
    --off: #3b434f;
    /* dark palette (default) */
    --surface: #12181f;
    --text: #e7ebf1;
    --muted: #8b94a2;
    --border: #222b36;
    --body: #333b45;
    --bezel: #4a5462;
    --outline: rgba(0, 0, 0, 0.3);
    --shadow: rgba(0, 0, 0, 0.6);
    --accent: #3b82f6;
    --header-tx: #ffffff;
    --plate: #3e4854;
    --screw: #abb4c1;
    --port-bg: #0a0e13;
    --display-bg: #080c11;
    --display-tx: #5cf0a2;
    --label: #ccd3dd;
  }
  :host([data-theme='light']) {
    --surface: #ffffff;
    --text: #0f172a;
    --muted: #64748b;
    --border: #e4e8ee;
    --body: #e9edf2;
    --bezel: #cdd4dd;
    --outline: rgba(15, 23, 42, 0.13);
    --shadow: rgba(15, 23, 42, 0.16);
    --accent: #2f6fce;
    --header-tx: #ffffff;
    --plate: #dce1e8;
    --screw: #98a1af;
    --port-bg: #12161c;
    --display-bg: #0d1117;
    --display-tx: #2eb473;
    --label: #5a636f;
  }
  svg {
    display: block;
  }
  text {
    font-family: 'IBM Plex Mono', 'SF Mono', ui-monospace, monospace;
  }

  /* ── live status: LEDs, spindle, display glow ─────────────────────────────
     driven by [data-state] on the <svg>. transitions/animations only touch
     fill/opacity/filter/transform (never layout), so they're cheap. */
  .led {
    fill: var(--off);
    transition: fill 0.35s ease;
  }
  .spin {
    transform-box: fill-box;
    transform-origin: center;
  }
  .disp-glow {
    opacity: 0.55;
    transition: opacity 0.35s;
  }
  [data-state='running'] .led--run {
    fill: var(--green);
    filter: drop-shadow(0 0 5px rgba(34, 197, 94, 0.85));
    animation: dev-breathe 2.6s ease-in-out infinite;
  }
  [data-state='running'] .led--com {
    fill: var(--blue);
    filter: drop-shadow(0 0 4px rgba(59, 130, 246, 0.85));
    animation: dev-blink 1.15s steps(1, end) infinite;
  }
  [data-state='running'] .led--twr-g {
    fill: var(--green);
    filter: drop-shadow(0 0 6px rgba(34, 197, 94, 0.9));
    animation: dev-breathe 2.6s ease-in-out infinite;
  }
  [data-state='running'] .spin {
    animation: dev-spin 1.5s linear infinite;
  }
  [data-state='running'] .disp-glow {
    opacity: 1;
    animation: dev-shimmer 3.4s ease-in-out infinite;
  }
  [data-state='error'] .led--err {
    fill: var(--red);
    filter: drop-shadow(0 0 5px rgba(239, 68, 68, 0.9));
    animation: dev-pulse 0.85s ease-in-out infinite;
  }
  [data-state='error'] .led--run {
    fill: var(--amber);
    opacity: 0.5;
  }
  [data-state='error'] .led--twr-r {
    fill: var(--red);
    filter: drop-shadow(0 0 6px rgba(239, 68, 68, 0.9));
    animation: dev-pulse 0.85s ease-in-out infinite;
  }
  [data-state='error'] .led--twr-a {
    fill: var(--amber);
    opacity: 0.7;
  }
  [data-state='error'] .disp-glow {
    opacity: 0.85;
  }
  [data-state='offline'] .disp-glow {
    opacity: 0.12;
  }
  @keyframes dev-breathe {
    0%,
    100% {
      opacity: 0.5;
    }
    50% {
      opacity: 1;
    }
  }
  @keyframes dev-blink {
    0%,
    45% {
      opacity: 1;
    }
    46%,
    100% {
      opacity: 0.12;
    }
  }
  @keyframes dev-pulse {
    0%,
    100% {
      opacity: 0.35;
    }
    50% {
      opacity: 1;
    }
  }
  @keyframes dev-spin {
    to {
      transform: rotate(360deg);
    }
  }
  @keyframes dev-shimmer {
    0%,
    100% {
      opacity: 0.6;
    }
    50% {
      opacity: 1;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .led,
    .spin,
    .disp-glow {
      animation: none !important;
    }
  }
`;
export class ThemedDeviceIcon extends LitElement {
    constructor() {
        super(...arguments);
        /** Live status — drives the LEDs / spindle / display glow. */
        this.state = 'idle';
        /** 0 → the glyph's aspect-correct default. */
        this.width = 0;
        this.height = 0;
    }
    connectedCallback() {
        super.connectedCallback();
        this.setAttribute('data-theme', getResolvedTheme());
        this.unsubTheme = onThemeChange((t) => this.setAttribute('data-theme', t));
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.unsubTheme?.();
    }
}
__decorate([
    property()
], ThemedDeviceIcon.prototype, "state", void 0);
__decorate([
    property({ type: Number })
], ThemedDeviceIcon.prototype, "width", void 0);
__decorate([
    property({ type: Number })
], ThemedDeviceIcon.prototype, "height", void 0);
//# sourceMappingURL=device-icon-base.js.map